/**
 * "Return for correction" — reason + e-signature, in one submit.
 *
 * Why not `ConfirmWithReason` with `requireEsign`: that flow verifies the password against
 * `esign_verification_and_log` in a separate call and then hands the action only a username
 * and a comment. `/bp/mbmr/return/` follows the server's preferred contract in
 * `services/approval/guards.py` — the password travels WITH the act, so the same request that
 * changes the record is the one that re-authenticates the signer, and there is no window in
 * which a verified signature is floating around unattached. The reason floor is the same idea
 * as `ConfirmWithReason`'s `MIN_REASON_LENGTH`, raised to the 10 characters every e-signed
 * remark in this product already requires; the server enforces the same number.
 *
 * The findings picker is the point of putting this in the editor rather than in a queue: a
 * reviewer returning a record because three operations carry no acceptance limits should tick
 * those three, not retype what the rail is already showing them. The server renders the
 * checked operations' blocking reasons into the audit comment, so the author reads the same
 * words the reviewer saw.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Input, Modal, Typography } from 'antd';
import { AlertTriangle, Lock, ShieldAlert, User } from 'lucide-react';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { MIN_REASON_LENGTH } from '@/components/ConfirmWithReason';

const { Text } = Typography;

/** The server's floor (`mbmr_return.MIN_REASON_LENGTH`), never below the shared one. */
export const MIN_RETURN_REASON = Math.max(10, MIN_REASON_LENGTH);

export default function ReturnForCorrectionModal({
  open,
  recipeName,
  scopeLabel,
  findings = [],
  submitting = false,
  error = null,
  onCancel,
  onSubmit,
}) {
  const { isDark } = useTheme();
  const [reason, setReason] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [checked, setChecked] = useState([]);
  const [touched, setTouched] = useState(false);

  // Every finding starts ticked: a reviewer opening this from the rail is almost always
  // returning the record *because* of them, and un-ticking is the rarer action.
  useEffect(() => {
    if (open) setChecked(findings.map((f) => f.operation_id));
  }, [open, findings]);

  useEffect(() => {
    if (!open) {
      setReason('');
      setUsername('');
      setPassword('');
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const reasonInvalid = trimmed.length < MIN_RETURN_REASON;
  const canSubmit = !reasonInvalid && username.trim() && password && !submitting;

  const muted = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const danger = isDark ? defaultColors.errorLight : defaultColors.error;

  const findingRows = useMemo(
    () =>
      findings.map((f) => ({
        value: f.operation_id,
        label: (
          <span style={{ fontSize: 12.5, lineHeight: 1.35 }}>
            <span style={{ fontWeight: 600 }}>
              {f.operation_number ? `${f.operation_number}. ` : ''}
              {f.operation_name}
            </span>
            <span style={{ color: muted, display: 'block' }}>
              {f.blocking_reasons?.[0] || 'Blocking check missing'}
              {f.blocking_reasons?.length > 1 ? ` (+${f.blocking_reasons.length - 1} more)` : ''}
            </span>
          </span>
        ),
      })),
    [findings, muted]
  );

  const submit = () => {
    setTouched(true);
    if (!canSubmit) return;
    onSubmit?.({
      reason: trimmed,
      esign: { username: username.trim(), password, comment: trimmed },
      finding_operation_ids: checked,
    });
  };

  return (
    <Modal
      open={open}
      centered
      maskClosable={false}
      destroyOnHidden
      width={620}
      zIndex={1600}
      title={
        <span className="inline-flex items-center gap-2">
          <ShieldAlert size={18} color={danger} />
          <span>Return this {scopeLabel} for correction</span>
        </span>
      }
      okText={submitting ? 'Returning…' : 'Return & sign'}
      cancelText="Cancel"
      okButtonProps={{ danger: true, disabled: !canSubmit, loading: submitting }}
      onOk={submit}
      onCancel={submitting ? undefined : onCancel}
    >
      <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12, color: muted }}>
        <strong style={{ color: isDark ? '#fff' : '#374151' }}>{recipeName}</strong> goes back to
        its author as <em>Review &amp; Not Approved</em> and becomes editable again. The author
        is notified, and this return is recorded in the audit trail with your name, the time and
        the reason below.
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          icon={<AlertTriangle size={16} />}
          style={{ marginBottom: 12 }}
          message={error.message}
          description={
            error.code === 'SOD_AUTHOR'
              ? 'Separation of duties: the person who authored a master record cannot return their own record.'
              : error.code === 'ROLE_REQUIRED'
              ? 'Returning an MBMR is a QA act and needs a QA-side role.'
              : null
          }
        />
      )}

      <Text strong style={{ fontSize: 13 }}>
        Reason for the return
      </Text>
      <Input.TextArea
        autoFocus
        rows={3}
        value={reason}
        maxLength={500}
        showCount
        onChange={(e) => setReason(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder="What has to change before this record can be approved? The author reads this verbatim."
        status={touched && reasonInvalid ? 'error' : undefined}
        data-testid="return-reason"
        style={{ marginTop: 6 }}
      />
      {touched && reasonInvalid && (
        <Text type="danger" style={{ fontSize: 12 }}>
          A reason of at least {MIN_RETURN_REASON} characters is required — the author has to be
          able to act on it.
        </Text>
      )}

      {findingRows.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <Text strong style={{ fontSize: 13 }}>
            Cite readiness findings ({checked.length} of {findingRows.length})
          </Text>
          <div style={{ fontSize: 12, color: muted, marginBottom: 6 }}>
            Ticked findings are written into the return so you do not have to retype them.
          </div>
          <div
            style={{
              maxHeight: 170,
              overflowY: 'auto',
              border: `1px solid ${isDark ? defaultColors.palette.gray[600] : '#e5e7eb'}`,
              borderRadius: 8,
              padding: '8px 10px',
            }}
          >
            <Checkbox.Group
              value={checked}
              onChange={setChecked}
              options={findingRows}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            />
          </div>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <Text strong style={{ fontSize: 13 }}>
          Electronic signature (21 CFR Part 11)
        </Text>
        <div style={{ fontSize: 12, color: muted, marginBottom: 6 }}>
          Re-enter your own credentials. The password is verified as part of this action, not
          before it.
        </div>
        <Input
          prefix={<User size={14} color={muted} />}
          placeholder="Your username"
          value={username}
          autoComplete="off"
          onChange={(e) => setUsername(e.target.value)}
          style={{ marginBottom: 8 }}
          data-testid="return-username"
        />
        <Input.Password
          prefix={<Lock size={14} color={muted} />}
          placeholder="Your password"
          value={password}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
          onPressEnter={submit}
          data-testid="return-password"
        />
      </div>
    </Modal>
  );
}
